
// src/components/HistoryItem/HistoryItem.jsx
import React, { useState, useEffect, useContext, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { UIContext } from '../../contexts/UIContext';
import { AuthContext } from '../../contexts/AuthContext';
import { HistoryContext } from '../../contexts/HistoryContext';
import ScoreDonut from '../../pages/VerificationPage/ScoreDonut'; 
import { getPlatformDisplayName } from '../PlatformUtils/PlatformUtils.jsx';
import './HistoryItem.css';

// Icons
import TextFieldsIcon from '@mui/icons-material/TextFields';
import ImageIcon from '@mui/icons-material/Image';
import AudiotrackIcon from '@mui/icons-material/Audiotrack';
import VideocamIcon from '@mui/icons-material/Videocam';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SearchIcon from '@mui/icons-material/Search';
import AddModeratorIcon from '@mui/icons-material/AddModerator';
import LinkIcon from '@mui/icons-material/Link';
import PublicIcon from '@mui/icons-material/Public';
import NotesIcon from '@mui/icons-material/Notes';
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined';
import GavelIcon from '@mui/icons-material/Gavel';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

// Material-UI Components
import {
    Tooltip,
    IconButton,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    OutlinedInput,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    Box,
    CircularProgress
} from '@mui/material';

// Helper: append "..." when backend preview is likely truncated at ~150 chars
const applyBackendEllipsis = (text) => {
    if (!text) return text;
    return `${text}${text.length > 150 - 10 ? '...' : ''}`;
};

// ContentPreview Helper Component
const ContentPreview = ({ preview, contentType, metadata }) => {
    const { t } = useTranslation();

    const isValidPreview = (src) => src && (src.startsWith('data:image') || src.startsWith('http'));
    // --- Build Dynamic Tooltip Content (Rich JSX matching License Page) ---
    const getTooltipContent = () => {
        // Data Sources
        const hasAiData = metadata && (metadata.summary || (metadata.safety_flags && metadata.safety_flags.length > 0));
        
        // If no AI data and no preview, don't show custom tooltip
        if (!hasAiData && !preview) return null;

        const categoryText = metadata?.category 
            ? t(`contentMetadata.inclusion.${metadata.category}`, { defaultValue: metadata.category }) 
            : "";

        // Translate Content Type
        const contentTypeLabel = t(`contentInput.contentTypes.${contentType}`, { defaultValue: contentType });

        // --- Determine Media Section Content ---
        let mediaSection;
        if (contentType === 'text') {
            mediaSection = (
                <div style={{ padding: '1rem', backgroundColor: 'rgba(0,0,0,0.2)', borderRadius: '6px' }}>
                    <p style={{
                        fontStyle: 'italic',
                        color: 'rgba(255, 255, 255, 0.8)',
                        lineHeight: 1.5,
                        fontSize: '0.9rem',
                        margin: 0,
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                    }}>
                        {preview ? applyBackendEllipsis(preview) : t('historyItem.preview.noPreview')}
                    </p>
                </div>
            );
        } else if (contentType === 'audio') {
            mediaSection = (
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    height: '100px', 
                    backgroundColor: 'rgba(0,0,0,0.2)', 
                    borderRadius: '6px',
                    color: 'rgba(255,255,255,0.4)'
                }}>
                    <AudiotrackIcon style={{ fontSize: '3rem', opacity: 0.7 }} />
                </div>
            );
        } else if ((contentType === 'image' || contentType === 'video') && isValidPreview(preview)) {
           mediaSection = (
                <div style={{ textAlign: 'center', borderRadius: '4px', overflow: 'hidden', backgroundColor: 'rgba(0,0,0,0.2)' }}>
                    <img
                        src={preview}
                        alt="Content preview"
                        style={{ maxWidth: '100%', maxHeight: '150px', objectFit: 'contain' }}
                    />
                </div>
            );
        } else {
             // Fallback for missing image or text string (e.g. "Preview not available")
             mediaSection = (
                <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center', 
                    height: '80px', 
                    backgroundColor: 'rgba(0,0,0,0.2)', 
                    borderRadius: '6px',
                    color: 'rgba(255,255,255,0.5)',
                    fontSize: '0.8rem',
                    fontStyle: 'italic',
                    textAlign: 'center',
                    padding: '0 1rem'
                }}>
                    {t('historyItem.preview.noPreview')}
                </div>
            );
        }

        return (
            <div style={{ maxWidth: '400px', fontFamily: 'Roboto, sans-serif', fontSize: '0.875rem' }}>
                
                {/* 1. Header & Media Section */}
                <div style={{ marginBottom: '12px' }}>
                    <h3 style={{
                        margin: '0 0 0.75rem 0',
                        fontSize: '0.9rem',
                        fontWeight: 600,
                        color: 'rgba(255, 255, 255, 0.85)',
                        textTransform: 'uppercase',
                        letterSpacing: '1px'
                    }}>
                        {t('verificationPage.contentInfoSection.preview')}
                        <span style={{ color: 'rgba(255, 255, 255, 0.5)', marginLeft: '4px' }}>
                            ({contentTypeLabel})
                        </span>
                    </h3>
                    
                    {mediaSection}
                </div>

                {/* 2. Description & Category */}
                {metadata?.summary && (
                    <div style={{ marginBottom: '8px' }}>
                        <div style={{ fontSize: '0.7rem', textTransform: 'uppercase', color: 'rgba(255, 255, 255, 0.5)', marginBottom: '4px', fontWeight: '600', paddingTop: '1rem', borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
                            {t('contentMetadata.descriptionLabel')}
                        </div>
                        <div style={{ fontSize: '0.85rem', lineHeight: '1.4', fontStyle: 'italic', color: 'rgba(255,255,255,0.9)' }}>
                            "{metadata.summary}"
                            {categoryText && (
                                <span style={{ color: 'var(--secondary-color)', fontWeight: 700, fontStyle: 'normal', marginLeft: '8px' }}>
                                    ({categoryText})
                                </span>
                            )}
                        </div>
                    </div>
                )}

                {/* 3. Safety Alert */}
                {metadata?.safety_flags && metadata.safety_flags.length > 0 && (
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        marginTop: '12px',
                        padding: '10px',
                        backgroundColor: 'rgba(139, 0, 0, 0.2)',
                        border: '1px solid rgba(139, 0, 0, 0.5)', 
                        borderRadius: '8px', 
                        color: '#F44336', 
                    }}>
                        <ReportProblemOutlinedIcon
                            sx={{ fontSize: '1rem', color: '#F44336', marginTop: '2px', flexShrink: 0 }}
                        />
                        <div style={{ fontSize: '0.8rem', lineHeight: '1.4' }}>
                            <strong style={{ color: '#F44336' }}>
                                {t('metaScores.safetyAlertPrefix')}
                            </strong>
                            {' '}
                            {metadata.safety_flags.map((flag, i) => (
                                <span key={flag}>
                                    {t(`contentMetadata.exclusion.${flag}`, { defaultValue: flag })}
                                    {i < metadata.safety_flags.length - 1 ? ', ' : ''}
                                </span>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const tooltipTitle = getTooltipContent();

    // Consistent tooltip styling matching LicenseManagementPage.css
    const tooltipProps = {
        tooltip: {
            sx: {
                maxWidth: 420,
                padding: '12px',
                backgroundColor: '#2D3748',
                border: '1px solid rgba(255, 255, 255, 0.15)',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
                borderRadius: '4px',
                fontSize: '0.875rem'
            }
        },
        arrow: {
            sx: {
                color: '#2D3748'
            }
        }
    };

    // --- Render Trigger (The small card preview) ---

    // 1. Handle non-text (Images, Video thumbnails, Audio)
    if (contentType !== 'text') {
        let triggerContent;
        if (contentType === 'audio') {
             // Audio placeholder trigger
             triggerContent = (
                <div className="history-item-preview-placeholder">
                    <AudiotrackIcon style={{ fontSize: '2rem', opacity: 0.5 }} />
                </div>
             );
        } else if ((contentType === 'image' || contentType === 'video') && isValidPreview(preview)) {
             triggerContent = <img src={preview} alt="Content preview" className="history-item-preview-image" />;
        } else {
             // Fallback text (e.g. "Preview not available")
             triggerContent = <div className="history-item-preview-placeholder">{t('historyItem.preview.noPreview')}</div>;
        }

        return (
            <Tooltip title={tooltipTitle} arrow placement="right" componentsProps={tooltipProps}>
                {triggerContent}
            </Tooltip>
        );
    }

    // 2. Handle Text (Small placeholder with truncated text)
    if (!preview) {
        return <div className="history-item-preview-placeholder">{t('historyItem.preview.noPreview')}</div>;
    }

    // Cap visible text in the CARD to 20 characters
    const TRUNCATE_LENGTH = 20;
    const isTruncated = preview.length > TRUNCATE_LENGTH;
    const textToShow = isTruncated ? `${preview.substring(0, TRUNCATE_LENGTH)}...` : preview;

    return (
        <Tooltip title={tooltipTitle} arrow placement="right" componentsProps={tooltipProps}>
            <div className="history-item-preview-placeholder text-preview">
                <p className="history-item-preview-text">{textToShow}</p>
            </div>
        </Tooltip>
    );
};

const calculateLocalVetraScore = (aiScore, fnScore) => {
    if (aiScore == null || fnScore == null) return null;
    const p_ai = aiScore / 100.0;
    const p_fn = fnScore / 100.0;
    const prob_or = 1 - ((1 - p_ai) * (1 - p_fn));
    const synergy_bonus = Math.pow(p_ai * p_fn, 0.5) * 0.15;
    return Math.floor(Math.min(prob_or + synergy_bonus, 1.0) * 100);
};

const getRiskLevel = (score) => {
    if (score == null) return 'minimal';
    if (score >= 90) return 'severe';
    if (score >= 75) return 'high';
    if (score >= 50) return 'moderate';
    if (score >= 25) return 'minor';
    if (score > 10) return 'low';
    return 'minimal';
};

// Main Component
const HistoryItem = ({ item, onShowExplanation, onShowHeatmap, onShowTextInspection, activeScoreFilters }) => {
    const { t, i18n } = useTranslation();
    const { fetchWithAuth } = useContext(AuthContext);
    const { setIsPageBlurred, openDocViewer } = useContext(UIContext);
    const { user } = useContext(AuthContext);
    const { tasksConfig, masterTaskOrder } = useContext(HistoryContext)
    const [isContextModalOpen, setIsContextModalOpen] = useState(false);
    const [isDownloadingReceipt, setIsDownloadingReceipt] = useState(false);
    const [isHoveringLocked, setIsHoveringLocked] = useState(false);

    // The history item object contains aggregated tasks.
    // We prioritize AI -> Fake News -> Any available task.
    const metadata = useMemo(() => {
        const tasks = item.tasks || {};
        return tasks['ai_detection']?.content_metadata ||
               tasks['fake_news_detection']?.content_metadata ||
               Object.values(tasks).find(t => t?.content_metadata)?.content_metadata || 
               null;
    }, [item.tasks]);

    // Check for safety flags
    const hasSafetyFlags = metadata?.safety_flags && metadata.safety_flags.length > 0;

    // Build Tooltip Content
    const safetyTooltipContent = hasSafetyFlags ? (
        <div style={{ padding: '4px' }}>
            <strong>{t('metaScores.safetyAlertPrefix')}</strong>
            <hr style={{ 
                margin: '6px 0 4px 0', 
                border: 'none', 
                borderTop: '1px solid rgba(255, 255, 255, 0.2)' 
            }} />
            
            <ul style={{ margin: '0', paddingLeft: '16px' }}>
                {metadata.safety_flags.map(flag => (
                    <li key={flag}>
                        {t(`contentMetadata.exclusion.${flag}`, { defaultValue: flag })}
                    </li>
                ))}
            </ul>
        </div>
    ) : "";

    // Optimized Blur Logic - only update context when modal state actively changes
    useEffect(() => {
        if (isContextModalOpen) {
            setIsPageBlurred(true);
        }

        return () => {
            if (isContextModalOpen) {
                setIsPageBlurred(false);
            }
        };
    }, [isContextModalOpen, setIsPageBlurred]);

    // --- Check for Context ---
    const userContext = item.user_context;
    const hasContext = userContext && (userContext.source_url || userContext.platform || userContext.user_notes);

    const augmentedTasks = useMemo(() => {
        // 1. Start with the raw tasks from the backend
        const raw = { ...(item.tasks || {}) };
        
        Object.entries(tasksConfig || {}).forEach(([key, config]) => {
            if (config.is_meta_task) {
                const deps = config.depends_on || [];
                
                // --- ONLY calculate if the backend hasn't provided this task ---
                // If raw[key] exists, it means the backend already sent the 
                // score and the JSON explanation object.
                if (!raw[key] && deps.length === 2 && raw[deps[0]] && raw[deps[1]]) {
                    const score1 = raw[deps[0]].score;
                    const score2 = raw[deps[1]].score;
                    const metaScore = calculateLocalVetraScore(score1, score2);
                    
                    raw[key] = {
                        score: metaScore,
                        likelihood: metaScore >= 50 ? 'Yes' : 'No',
                        // Create a valid fallback structured explanation mapping to proper keys
                        explanation: { 
                            overall_verdict: getRiskLevel(metaScore),
                            contributing_factors: {
                                [deps[0]]: { score: score1, level: getRiskLevel(score1) },
                                [deps[1]]: { score: score2, level: getRiskLevel(score2) }
                            }
                        },
                        is_meta: true
                    };
                } else if (raw[key]) {
                    // If it exists from the backend, just ensure the is_meta flag is set
                    raw[key].is_meta = true;
                }
            }
        });
        return raw;
    }, [item.tasks, tasksConfig]);

    // 1. Sort and filter tasks dynamically based on the YAML order
    const tasksToShowInDropdown = useMemo(() => {
        if (!masterTaskOrder) return [];
        // return masterTaskOrder.filter(task => item.tasks[task] && activeScoreFilters.includes(task));
        return masterTaskOrder.filter(task => augmentedTasks[task] && activeScoreFilters.includes(task));
    }, [activeScoreFilters, masterTaskOrder, augmentedTasks]);

    // 2. Select the first available Meta-Task by default. If none, select the first base task.
    const [activeTaskKey, setActiveTaskKey] = useState(() => {
        const firstMeta = tasksToShowInDropdown.find(t => tasksConfig?.[t]?.is_meta_task);
        return firstMeta || tasksToShowInDropdown[0] || null;
    });

    const prevTasksRef = useRef(tasksToShowInDropdown);

    // 3. Make Display Name fetcher completely dynamic
    const getTaskDisplayName = (taskKey) => {
        return t(`resultDisplay.analysisTasks.${taskKey}`, { 
            defaultValue: tasksConfig?.[taskKey]?.display_name || taskKey 
        });
    };

    useEffect(() => {
        const prevTasks = prevTasksRef.current;
        const arraysEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b);
        const tasksChanged = !arraysEqual(prevTasks, tasksToShowInDropdown);

        if (tasksChanged) {
            prevTasksRef.current = tasksToShowInDropdown;

            // Restores the best Meta-Task when filters are cleared
            const bestAvailableOption = tasksToShowInDropdown.find(t => tasksConfig?.[t]?.is_meta_task) || tasksToShowInDropdown[0] || null;

            if (bestAvailableOption && activeTaskKey !== bestAvailableOption) {
                setActiveTaskKey(bestAvailableOption);
            }
        } else {
            if (!tasksToShowInDropdown.includes(activeTaskKey)) {
                const bestAvailableOption = tasksToShowInDropdown[0] || null;
                if (bestAvailableOption) setActiveTaskKey(bestAvailableOption);
            }
        }
    }, [tasksToShowInDropdown, activeTaskKey, tasksConfig]);

    const activeResult = augmentedTasks[activeTaskKey];
    if (!activeResult) return null;

    // --- DYNAMIC LEDGER LOGIC ---
    const isMetaTask = !!tasksConfig?.[activeTaskKey]?.is_meta_task;
    
    let ledgerInfo = {
        hasReceipt: false,
        versionId: null,
        logId: null,
        syncedAt: null,
        analysisAt: null,
        tooltipMessage: '',
        isMetaMismatch: false
    };

    if (activeResult) {
        if (isMetaTask) {
            // For Meta-Tasks, we must ensure all underlying base tasks share the EXACT SAME receipt
            const deps = tasksConfig[activeTaskKey].depends_on || [];
            const depResults = deps.map(d => augmentedTasks[d]).filter(Boolean);
            
            const allHaveReceipts = depResults.length === deps.length && depResults.every(r => r.s3_receipt_version_id);
            const noneHaveReceipts = depResults.every(r => !r.s3_receipt_version_id);
            const allShareSameReceipt = allHaveReceipts && depResults.every(r => r.s3_receipt_version_id === depResults[0].s3_receipt_version_id);

            if (allShareSameReceipt) {
                // CASE 1: All notarized and matching
                ledgerInfo = {
                    hasReceipt: true,
                    versionId: depResults[0].s3_receipt_version_id,
                    logId: depResults[0].log_id,
                    syncedAt: depResults[0].ledger_synced_at,
                    analysisAt: depResults[0].analysis_timestamp, 
                };
            } else if (noneHaveReceipts) {
                // CASE 2: None are notarized yet (The missing logic)
                ledgerInfo.tooltipMessage = t('historyItem.ledger.pendingSync');
                ledgerInfo.isMetaMismatch = false; // Ensure no glow
            } else {
                // CASE 3: Some are notarized and others aren't, OR they have different IDs
                ledgerInfo.tooltipMessage = t('historyItem.ledger.metaTaskMismatch');
                ledgerInfo.isMetaMismatch = true; // Trigger glow
            }
        } else {
            // For standard tasks, just read its specific receipt data
            if (activeResult.s3_receipt_version_id) {
                ledgerInfo = {
                    hasReceipt: true,
                    versionId: activeResult.s3_receipt_version_id,
                    logId: activeResult.log_id,
                    syncedAt: activeResult.ledger_synced_at,
                    analysisAt: activeResult.analysis_timestamp,
                };
            } else {
                ledgerInfo.tooltipMessage = t('historyItem.ledger.pendingSync');
            }
        }
    }

    const hasInspectableRegions =
        (item.content_type === 'image' && activeResult.manipulation_regions && activeResult.manipulation_regions?.length > 0) ||
        (item.content_type === 'text' && activeResult.manipulation_regions && activeResult.manipulation_regions?.length > 0) ||
        (item.content_type === 'video' && activeResult.manipulation_regions && activeResult.manipulation_regions?.boxes?.length > 0);

    const explanation = activeResult?.explanation || "";

    const subScores = {};
    Object.keys(augmentedTasks).forEach(key => {
        const res = augmentedTasks[key];
        const isMeta = res && (typeof res.explanation === 'object' || typeof res.explanation_text === 'object');
        if (!isMeta) {
            subScores[key] = res?.score;
        }
    });

    // --- Update Verification URL Logic ---
    let verificationUrl = `/verify?id=${item.exact_hash}`;
    // Append Attribution (Ref)
    if (user?.public_id) {
        verificationUrl += `&ref=${user.public_id}`;
    }
    // Append Context
    if (item.context_hash) {
        verificationUrl += `&ctx=${item.context_hash}`;
    }

    const formatTimestamp = (isoString) => {
        if (!isoString) return 'N/A';
        const date = new Date(isoString);
        // Use i18n.language to tell toLocaleString which language to use!
        return date.toLocaleString(i18n.language, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    };

    const handleTaskChange = (event) => setActiveTaskKey(event.target.value);

    // --- Smart Filename Truncation ---
    const FILENAME_TRUNCATE_LENGTH = 25;
    let displayFilename;
    let fullFilename;

    if (item.source_filename) {

        // 1. Clean the filename (remove the 6-char hash added by backend to avoid S3 collisions)
        // Matches "-a1b2c3.ext" at the end and replaces with ".ext"
        fullFilename = item.source_filename.replace(/-[a-f0-9]{6}(\.[a-z0-9]+)$/i, '$1');
        
        // 2. Match "Scraped <Platform> Post" and translate dynamically using centralized utils
        const scrapedMatch = fullFilename.match(/^Scraped\s+(\w+)\s+Post$/i);
        if (scrapedMatch) {
            const platformName = scrapedMatch[1];
            const translatedPlatform = getPlatformDisplayName(platformName, t);
            fullFilename = t('ThreatItem.scrapedPost', { 
                platform: translatedPlatform, 
                defaultValue: `Scraped ${translatedPlatform} Post` 
            });
        }

        const lastDotIndex = fullFilename.lastIndexOf('.');

        // Check if there is an extension and the basename is too long
        if (lastDotIndex > 0 && fullFilename.substring(0, lastDotIndex).length > FILENAME_TRUNCATE_LENGTH) {
            const basename = fullFilename.substring(0, lastDotIndex);
            const extension = fullFilename.substring(lastDotIndex);
            displayFilename = basename.substring(0, FILENAME_TRUNCATE_LENGTH) + '... ' + extension;
        }
        // Handle files with no extension that are too long
        else if (lastDotIndex === -1 && fullFilename.length > FILENAME_TRUNCATE_LENGTH) {
            displayFilename = fullFilename.substring(0, FILENAME_TRUNCATE_LENGTH) + '... ';
        }
        // Otherwise, use the original filename
        else {
            displayFilename = fullFilename;
        }
    } else {
        // Fallback for content without a filename (e.g., text analysis)
        fullFilename = t('historyItem.preview.analyzedType', { contentType: item.content_type });
        displayFilename = fullFilename;
    }

    const labelId = `analysis-type-label-${item.exact_hash}-${item.context_hash || 'no-ctx'}`;
    const selectId = `analysis-type-select-${item.exact_hash}-${item.context_hash || 'no-ctx'}`;

    // Function to handle receipt download
    const handleDownloadReceipt = async (e, logIdToDownload) => {
        e.preventDefault();
        if (isDownloadingReceipt) return;
        setIsDownloadingReceipt(true);

        try {
            // Fetch using the specific log_id for the active task
            const response = await fetchWithAuth(`/api/v1/ledger/receipt/${logIdToDownload}`);
            if (!response.ok) {
                console.error("Failed to download receipt");
                return;
            }
            
            // Try to extract the filename from the headers, fallback to a default
            let filename = `Vetra_AI_Trust_Receipt_${logIdToDownload}.json`;
            const disposition = response.headers.get('Content-Disposition');
            if (disposition && disposition.indexOf('filename=') !== -1) {
                const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
                if (matches != null && matches[1]) { 
                    filename = matches[1].replace(/['"]/g, '');
                }
            }

            // Create a Blob and trigger a silent download
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);

            // Clean up memory
            setTimeout(() => window.URL.revokeObjectURL(url), 10000);
        } catch (err) {
            console.error("Error downloading receipt:", err);
        } finally {
            setIsDownloadingReceipt(false);
        }
    };

    return (
        <div className="history-item-card">
            <Tooltip
                title={t('historyItem.actions.viewVerification')}
                arrow
                PopperProps={{
                    style: { zIndex: 9999 }
                }}
            >
                <IconButton
                    component={Link}
                    to={verificationUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="verification-btn"
                    aria-label={t('historyItem.actions.viewVerification')}
                >
                    <img
                        src="/static/vetraai_logo_grey.webp"
                        alt="Verification Shield"
                        className="shield-icon-image-history"
                    />
                </IconButton>
            </Tooltip>

            <div className="history-item-main-info">
            <div className="history-item-preview-link">
                    {/* Preview container now includes the top-left badge (type-badge-mini) */}
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                        <ContentPreview 
                            preview={item.content_preview} 
                            contentType={item.content_type} 
                            metadata={metadata}
                        />
                        <div className="type-badge-mini" aria-hidden>
                            {item.content_type === 'video' && <VideocamIcon fontSize="inherit" />}
                            {item.content_type === 'image' && <ImageIcon fontSize="inherit" />}
                            {item.content_type === 'audio' && <AudiotrackIcon fontSize="inherit" />}
                            {item.content_type === 'text' && <TextFieldsIcon fontSize="inherit" />}
                        </div>
                    </div>
                </div>
                <div className="history-item-details">
                    <div className="history-filename-row">
                        <Tooltip 
                            title={fullFilename} 
                            arrow 
                            placement="top" 
                            disableHoverListener={fullFilename === displayFilename}
                            PopperProps={{ className: 'global-dark-tooltip' }}
                        >
                            <span className="history-item-filename" style={{ cursor: fullFilename !== displayFilename ? 'help' : 'default' }}>
                                {displayFilename}
                            </span>
                        </Tooltip>
                        {hasContext && (
                            <Tooltip title={t('historyItem.context.viewContext')} arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => setIsContextModalOpen(true)}
                                    className="context-indicator-btn"
                                >
                                    <AddModeratorIcon fontSize="inherit" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </div>
                    <span className="history-item-timestamp">
                        {formatTimestamp(item.latest_timestamp)}
                    </span>
                    {/* --- DYNAMIC LEDGER BADGE --- */}
                    {ledgerInfo.hasReceipt ? (
                        <div className="ledger-info-row">
                            <Tooltip 
                                interactive
                                title={
                                    <div style={{ padding: '4px' }}>
                                        <div style={{ fontWeight: 'bold', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            {t('historyItem.ledger.securedTitle')}
                                            
                                            {/* --- Nested Tooltip --- */}
                                            <Tooltip 
                                                title={t('historyItem.ledger.learnMore')} 
                                                arrow 
                                                placement="top"
                                                PopperProps={{ className: 'global-dark-tooltip' }}
                                            >
                                                <IconButton 
                                                    size="small" 
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        openDocViewer('reference/immutable_trust_ledger.md');
                                                    }}
                                                    sx={{ 
                                                        padding: '2px', 
                                                        color: 'var(--secondary-color)', 
                                                        transition: 'all 0.2s ease-in-out',
                                                        '&:hover': { 
                                                            color: 'var(--secondary-color)',
                                                            backgroundColor: 'rgba(79, 195, 247, 0.1)' 
                                                        } 
                                                    }}
                                                >
                                                    <HelpOutlineIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>

                                        </div>
                                        {/* Use ledgerInfo timestamps instead of row-level timestamps */}
                                        <div style={{ fontSize: '0.75rem', opacity: 0.9, marginTop: '8px', marginBottom: '4px' }}>
                                            • {t('historyItem.ledger.analysisDate')}: {formatTimestamp(ledgerInfo.analysisAt)}
                                        </div>
                                        <div style={{ fontSize: '0.75rem', opacity: 0.9}}>
                                            • {t('historyItem.ledger.notarizationDate')}: {formatTimestamp(ledgerInfo.syncedAt)}
                                        </div>
                                    </div>
                                } 
                                arrow
                                PopperProps={{ className: 'global-dark-tooltip' }}
                            >
                                <div className="ledger-status-badge-mini">
                                    <GavelIcon sx={{ fontSize: '0.8rem', mr: 0.3 }} />
                                    {t('historyItem.ledger.verifiable')}
                                </div>
                            </Tooltip>
                            <Tooltip title={t('historyItem.ledger.downloadReceipt')} arrow>
                                <IconButton 
                                    onClick={(e) => handleDownloadReceipt(e, ledgerInfo.logId)}
                                    className="ledger-download-btn-mini"
                                    size="small"
                                    disabled={isDownloadingReceipt}
                                >
                                    {isDownloadingReceipt ? (
                                        <CircularProgress size={16} sx={{ color: 'var(--secondary-color)' }} />
                                    ) : (
                                        <FileDownloadIcon sx={{ fontSize: '1rem' }} />
                                    )}
                                </IconButton>
                            </Tooltip>
                        </div>
                    ) : (
                        /* Greyed out badge if no single receipt applies (Meta-Task mismatch or pending sync) */
                        <div 
                            className="ledger-info-row" 
                            style={{ opacity: 0.4 }}
                            onMouseEnter={() => setIsHoveringLocked(true)}
                            onMouseLeave={() => setIsHoveringLocked(false)}
                        >
                            <Tooltip 
                                title={ledgerInfo.tooltipMessage} 
                                arrow 
                                PopperProps={{ className: 'global-dark-tooltip' }}
                            >
                                <div className="ledger-status-badge-mini" style={{ cursor: 'help', filter: 'grayscale(1)' }}>
                                    <GavelIcon sx={{ fontSize: '0.8rem', mr: 0.3 }} />
                                    {t('historyItem.ledger.verifiable')}
                                </div>
                            </Tooltip>
                        </div>
                    )}
                    {hasSafetyFlags && (
                        <Tooltip 
                            title={safetyTooltipContent} 
                            arrow 
                            placement="top"
                        >
                            <span className="safety-alert-badge">
                                <ReportProblemOutlinedIcon />
                                {t('historyItem.safetyAlertBadge')}
                            </span>
                        </Tooltip>
                    )}
                </div>
            </div>

            <div className="history-item-results-section">
                {tasksToShowInDropdown.length > 1 ? (
                    <FormControl 
                        size="small" 
                        className={`history-task-select-form ${isHoveringLocked && ledgerInfo.isMetaMismatch ? 'highlighted-dropdown' : ''}`}
                    >
                        <InputLabel id={labelId}>{t('historyItem.labels.selectScore')}</InputLabel>
                        <Select
                            labelId={labelId}
                            id={selectId}
                            value={activeTaskKey}
                            onChange={handleTaskChange}
                            className="history-task-select"
                            input={<OutlinedInput label={t('historyItem.labels.selectScore')} />}
                            renderValue={(selected) => {
                                const isMeta = tasksConfig?.[selected]?.is_meta_task;
                                return (
                                    <div className="menu-item-with-icon">
                                        {isMeta && <img src="/static/vetraai_logo_grey.webp" alt="Meta Score" className="menu-item-logo-icon" />}
                                        <span>{getTaskDisplayName(selected)}</span>
                                    </div>
                                );
                            }}
                        >
                            {tasksToShowInDropdown.map(taskKey => {
                                const isMeta = tasksConfig?.[taskKey]?.is_meta_task;
                                return (
                                    <MenuItem key={taskKey} value={taskKey}>
                                        <div className="menu-item-with-icon">
                                            {isMeta && <img src="/static/vetraai_logo_dark_blue.webp" alt="Meta Score" className="menu-item-logo-icon" />}
                                            <span>{getTaskDisplayName(taskKey)}</span>
                                        </div>
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>
                ) : (
                    /* Stylized rectangle mimicking the Select input field but static */
                    <Box 
                        className={`history-task-select-form ${isHoveringLocked && ledgerInfo.isMetaMismatch ? 'highlighted-dropdown' : ''}`}
                        sx={{ 
                            height: '40px', // Exactly matches the height of the size="small" Select
                            display: 'flex', 
                            alignItems: 'center', 
                            padding: '0 14px', // Matches standard MUI OutlinedInput padding
                            borderRadius: '4px', 
                            border: '1px solid rgba(255, 255, 255, 0.23)', // Matches standard input border
                            boxSizing: 'border-box'
                        }}
                    >
                        <div className="menu-item-with-icon">
                            {tasksConfig?.[activeTaskKey]?.is_meta_task && (
                                <img src="/static/vetraai_logo_grey.webp" alt="Meta Score" className="menu-item-logo-icon" />
                            )}
                            <span style={{ fontSize: '1rem', color: 'white', letterSpacing: '0.00938em' }}>
                                {getTaskDisplayName(activeTaskKey)}
                            </span>
                        </div>
                    </Box>
                )}

                <div className="history-item-result-display">
                    <Tooltip 
                        title={explanation 
                            ? t('historyItem.actions.viewExplanation') 
                            : t('historyItem.tooltips.explanationNotAvailable')
                        } 
                        arrow
                    >
                        <span style={{ display: 'inline-block' }}>
                            <IconButton
                                className="explanation-icon-btn"
                                disabled={!explanation}
                                onClick={() => {
                                    if (!explanation) return;
                                    const isMetaTask = !!tasksConfig?.[activeTaskKey]?.is_meta_task;
                                    onShowExplanation(
                                        getTaskDisplayName(activeTaskKey, t),
                                        activeResult.explanation, 
                                        isMetaTask,
                                        activeResult.score,
                                        activeTaskKey
                                    );
                                }}
                                aria-label={t('historyItem.actions.viewExplanation')}
                                sx={{
                                    '&.Mui-disabled': {
                                        color: 'rgba(255, 255, 255, 0.15)'
                                    }
                                }}
                            >
                                <InfoOutlinedIcon />
                            </IconButton>
                        </span>
                    </Tooltip>
                    {(() => {
                        const isMetaTask = !!tasksConfig?.[activeTaskKey]?.is_meta_task;
                        const wasRegionsEnabled = activeResult?.manipulation_regions !== null && activeResult?.manipulation_regions !== undefined;
                        
                        // Determine dynamic tooltip text based on WHY it's disabled
                        let inspectTooltipText;
                        if (isMetaTask) {
                            // --- NEW: Explicitly explain that meta-tasks don't have heatmaps ---
                            inspectTooltipText = t('historyItem.tooltips.metaTaskRegionsDisabled', { 
                                defaultValue: 'Inspectable regions are not available for combined meta-scores. Please select an individual base task from the dropdown to view them.' 
                            });
                        } else if (item.content_type === 'audio') {
                            inspectTooltipText = t('table.tooltips.regionsDisabledForAudio', { defaultValue: 'Region detection is not available for audio.' });
                        } else if (!wasRegionsEnabled) {
                            inspectTooltipText = t('verificationPage.tooltips.regionsDisabled');
                        } else if (!hasInspectableRegions) {
                            inspectTooltipText = t('resultDisplay.noRegionsFlagged', { taskName: getTaskDisplayName(activeTaskKey) });
                        } else {
                            inspectTooltipText = t('historyItem.actions.inspectDetails');
                        }

                        return (
                            <Tooltip title={inspectTooltipText} arrow>
                                {/* Wrapper span required because disabled MUI buttons don't fire tooltip hover events */}
                                <span style={{ display: 'inline-block' }}>
                                    <IconButton
                                        className="explanation-icon-btn" 
                                        disabled={!hasInspectableRegions}
                                        onClick={() => {
                                            if (item.content_type === 'image') {
                                                onShowHeatmap(
                                                    item.content_preview, 
                                                    activeResult.manipulation_regions,
                                                    getTaskDisplayName(activeTaskKey),
                                                    activeResult.score
                                                );
                                            } else if (item.content_type === 'video') {
                                                const videoNote = `The Heatmap shows flagged regions from the most suspicious frame at ${activeResult.manipulation_regions.key_frame_time.toFixed(2)} seconds.`;
                                                onShowHeatmap(
                                                    activeResult.manipulation_regions.key_frame_thumbnail,
                                                    activeResult.manipulation_regions.boxes,
                                                    getTaskDisplayName(activeTaskKey),
                                                    activeResult.score,
                                                    videoNote
                                                );
                                            } else if (item.content_type === 'text') {
                                                onShowTextInspection(
                                                    activeResult.manipulation_regions,
                                                    getTaskDisplayName(activeTaskKey),
                                                    activeResult.score
                                                );
                                            }
                                        }}
                                        aria-label={t('historyItem.actions.inspectDetails')}
                                        sx={{
                                            // Make the disabled state look distinct but match the theme
                                            '&.Mui-disabled': {
                                                color: 'rgba(255, 255, 255, 0.15)'
                                            }
                                        }}
                                    >
                                        <SearchIcon />
                                    </IconButton>
                                </span>
                            </Tooltip>
                        );
                    })()}
                    <div className="result-pair">
                        <span className="result-label">{t('historyItem.labels.likelihood')}</span>
                        <div className="result-value likelihood">
                            {activeResult.likelihood === 'Yes' ?
                                <WarningIcon className="icon-yes" /> :
                                <CheckCircleIcon className="icon-no" />
                            }
                            <span>{activeResult.likelihood}</span>
                        </div>
                    </div>
                    <div className="result-pair score-pair" style={{ alignItems: 'center' }}>                       
                        <ScoreDonut 
                            riskScore={activeResult.score}
                            scoreType={activeTaskKey}
                            t={t}
                            size={50} // Small size
                            compact={true} // New prop
                        />
                    </div>
                </div>
            </div>

            {/* --- CONTEXT DETAIL MODAL --- */}
            {hasContext && (
                <Dialog
                    open={isContextModalOpen}
                    onClose={() => setIsContextModalOpen(false)}
                    PaperProps={{
                        sx: {
                            // FORCE white background and dark text with !important
                            backgroundColor: '#ffffff !important',
                            color: '#1e293b !important',
                            minWidth: '400px',
                            borderRadius: '12px',
                            backgroundImage: 'none !important',     // Kill any dark mode gradients
                            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)' // Add a subtle shadow for depth
                        }
                    }}
                    // Ensure the modal has a high z-index to sit above the blurred content
                    style={{ zIndex: 1400 }}
                >
                    {/* Updated Title Key */}
                    <DialogTitle sx={{ borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 1, color: '#0f172a' }}>
                        <AddModeratorIcon sx={{ color: 'var(--secondary-color)' }} />
                        {t('historyItem.context.modalTitle')}
                    </DialogTitle>

                    <DialogContent sx={{ mt: 2 }}>
                        {/* Source URL */}
                        {userContext.source_url && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, fontWeight: 'bold' }}>
                                    <LinkIcon fontSize="small" /> {t('contentInput.context.sourceUrlLabel')}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    sx={{
                                        wordBreak: 'break-all',
                                        fontFamily: 'monospace',
                                        bgcolor: '#f1f5f9',
                                        color: '#334155',
                                        p: 1,
                                        borderRadius: 1,
                                        border: '1px solid #e2e8f0',
                                        // Styling for the link inside
                                        '& a': {
                                            color: '#334155', // Match other inputs by default
                                            textDecoration: 'none',
                                            transition: 'color 0.2s ease'
                                        },
                                        '& a:hover': {
                                            textDecoration: 'underline'
                                        }
                                    }}>

                                    <a href={userContext.source_url} target="_blank" rel="noopener noreferrer">
                                        {userContext.source_url}
                                    </a>
                                </Typography>
                            </Box>
                        )}

                        {/* Platform */}
                        {userContext.platform && (
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, fontWeight: 'bold' }}>
                                    <PublicIcon fontSize="small" /> {t('contentInput.context.platformLabel')}
                                </Typography>
                                <Typography variant="body2" sx={{ bgcolor: '#f1f5f9', color: '#334155', p: 1, borderRadius: 1, border: '1px solid #e2e8f0' }}>
                                    {getPlatformDisplayName(userContext.platform, t)}
                                </Typography>
                            </Box>
                        )}

                        {/* Notes */}
                        {userContext.user_notes && (
                            <Box>
                                <Typography variant="caption" sx={{ color: '#64748b', display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, fontWeight: 'bold' }}>
                                    <NotesIcon fontSize="small" /> {t('contentInput.context.userNotesLabel')}
                                </Typography>
                                <Typography variant="body2" sx={{ bgcolor: '#f1f5f9', color: '#334155', p: 1, borderRadius: 1, fontStyle: 'italic', border: '1px solid #e2e8f0' }}>
                                    "{userContext.user_notes}"
                                </Typography>
                            </Box>
                        )}
                    </DialogContent>

                    <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
                        {/* Validated Cancel Key */}
                        <Button onClick={() => setIsContextModalOpen(false)} sx={{ color: '#64748b', '&:hover': { color: '#334155', backgroundColor: '#f1f5f9' } }}>
                            {t('historyItem.context.cancel')}
                        </Button>
                    </DialogActions>
                </Dialog>
            )}
        </div>
    );
};

export default HistoryItem;